import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
  {
    id: 1,
    name: "Sarah Chen",
    role: "Software Developer",
    company: "TechStart AS",
    avatar: "https://images.unsplash.com/photo-1646041805292-fd77781436f9",
    avatarAlt: "Professional headshot of Asian woman with shoulder-length black hair wearing navy blazer",
    content: `Workfolio changed my career completely. As someone with a non-traditional background, 
      I was constantly overlooked because of my name and photo. Through Workfolio's anonymous system, 
      I landed my dream job at a top tech company based purely on my skills.`,
    rating: 5,
    type: "job-seeker"
  },
  {
    id: 2,
    name: "Erik Andersen",
    role: "HR Director",
    company: "Nordic Solutions",
    avatar: "https://images.unsplash.com/photo-1584598665938-079ecb78d906",
    avatarAlt: "Professional headshot of Scandinavian man with blonde hair and beard in dark suit",
    content: `We've hired 12 people through Workfolio in the past year, and our team diversity 
      has improved dramatically. The pay-per-unlock model is perfect for our budget, and the quality 
      of candidates is exceptional. It's revolutionized our hiring process.`,
    rating: 5,
    type: "employer"
  },
  {
    id: 3,
    name: "Amira Hassan",
    role: "UX Designer",
    company: "Design Studio Oslo",
    avatar: "https://images.unsplash.com/photo-1706948742922-f89fd69d5fa5",
    avatarAlt: "Professional headshot of Middle Eastern woman with dark hair wearing white blouse",
    content: `After months of job searching with no responses, I tried Workfolio. Within two weeks, 
      I had three companies interested in my profile. The anonymous approach really works - 
      employers focus on what matters: your skills and experience.`,
    rating: 5,
    type: "job-seeker"
  },
  {
    id: 4,
    name: "Lars Petersen",
    role: "Startup Founder",
    company: "GreenTech Innovations",
    avatar: "https://images.unsplash.com/photo-1641479160067-5ae7bde244b0",
    avatarAlt: "Professional headshot of young Scandinavian man with brown hair in casual shirt",
    content: `As a startup, we need to hire smart and fast. Workfolio's platform helped us find 
      incredible talent we never would have discovered through traditional channels. The 149 NOK 
      unlock fee is nothing compared to recruitment agency costs.`,
    rating: 5,
    type: "employer"
  }];


  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials?.length]);

  const handlePrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
  };

  const handleDotClick = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials?.[currentIndex];

  return (
    <section className="py-20 lg:py-32 bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-warning/10 text-warning text-sm font-medium mb-6">
            <Icon name="MessageCircle" size={16} className="mr-2" />
            Success Stories
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            What Our Users Say
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Real stories from job seekers who found their dream jobs and employers 
            who discovered exceptional talent through our platform.
          </p>
        </div>
        
        {/* Main Testimonial */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-3xl p-8 lg:p-12 shadow-elevation-lg border border-border relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl"></div>
            
            {/* Quote Icon */}
            <div className="absolute top-8 left-8 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Quote" size={24} className="text-primary" />
            </div>
            
            <div className="relative z-10">
              {/* Content */}
              <div className="text-center mb-8">
                <p className="text-lg lg:text-xl text-foreground leading-relaxed mb-6 italic">
                  "{currentTestimonial?.content}"
                </p>
                
                {/* Rating */}
                <div className="flex justify-center space-x-1 mb-6">
                  {[...Array(currentTestimonial?.rating)]?.map((_, i) =>
                  <Icon key={i} name="Star" size={20} className="text-warning fill-current" />
                  )}
                </div>
              </div>
              
              {/* Author */}
              <div className="flex items-center justify-center space-x-4">
                <Image
                  src={currentTestimonial?.avatar}
                  alt={currentTestimonial?.avatarAlt}
                  className="w-16 h-16 rounded-full object-cover border-2 border-border" />

                
                <div className="text-left">
                  <div className="font-semibold text-foreground text-lg">
                    {currentTestimonial?.name}
                  </div>
                  <div className="text-muted-foreground">
                    {currentTestimonial?.role}
                  </div>
                  <div className="text-sm text-primary font-medium">
                    {currentTestimonial?.company}
                  </div>
                </div>
                
                {/* Type Badge */}
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                currentTestimonial?.type === 'job-seeker' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`
                }>
                  {currentTestimonial?.type === 'job-seeker' ? 'Job Seeker' : 'Employer'}
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              className="w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all shadow-elevation"
              aria-label="Previous testimonial">

              <Icon name="ChevronLeft" size={20} />
            </button>
            
            {/* Dots */}
            <div className="flex space-x-2">
              {testimonials?.map((_, index) =>
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex ?
                'bg-primary scale-125' : 'bg-border hover:bg-muted-foreground'}`
                }
                aria-label={`Go to testimonial ${index + 1}`} />

              )}
            </div>
            
            {/* Next Button */}
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all shadow-elevation"
              aria-label="Next testimonial">

              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
          
          {/* Auto-play indicator */}
          <div className="text-center mt-4">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors">

              {isAutoPlaying ?
              <span className="flex items-center justify-center space-x-2">
                  <Icon name="Pause" size={16} />
                  <span>Pause auto-play</span>
                </span> :

              <span className="flex items-center justify-center space-x-2">
                  <Icon name="Play" size={16} />
                  <span>Resume auto-play</span>
                </span>
              }
            </button>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;